// Copyright (c) Ping. All rights reserved.

import React, { useCallback } from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  type TextStyle,
  View,
  type ViewStyle,
} from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { createTheme } from '../../design-system/theme';
import { tasksStrings } from './strings';
import type { TaskItem, TaskPriority, TaskSelection } from './types';

export interface TaskRowProps {
  readonly task: TaskItem;
  readonly selection: TaskSelection;
  readonly onToggleCompletion: (task: TaskItem) => void;
  readonly onPress?: ((task: TaskItem) => void) | undefined;
  readonly onToggleSelection?: ((task: TaskItem) => void) | undefined;
  readonly onLongPress?: ((task: TaskItem) => void) | undefined;
  /** Override `Date.now()` injection for stable rendering in tests. */
  readonly nowMs?: number;
}

const PRIORITY_TONE: Record<TaskPriority, 'rose' | 'amber' | 'sky' | 'mint'> = {
  urgent: 'rose',
  high: 'amber',
  medium: 'sky',
  low: 'mint',
};

/** Single task row with checkbox, title, optional priority + due pills. */
export const TaskRow = React.memo<TaskRowProps>(
  ({ task, selection, onToggleCompletion, onPress, onToggleSelection, onLongPress, nowMs }) => {
    const scheme = useColorScheme();
    const theme = createTheme(scheme === 'dark' ? 'dark' : 'light');
    const completed = task.state === 'completed';
    const inSelectionMode = selection.mode === 'active';
    const isSelected = inSelectionMode && selection.selectedIds.has(task.id);

    const handlePress = useCallback(() => {
      if (inSelectionMode) {
        onToggleSelection?.(task);
        return;
      }
      onPress?.(task);
    }, [inSelectionMode, onPress, onToggleSelection, task]);

    const handleLongPress = useCallback(() => {
      onLongPress?.(task);
    }, [onLongPress, task]);

    const handleCheckboxPress = useCallback(() => {
      if (inSelectionMode) {
        onToggleSelection?.(task);
        return;
      }
      onToggleCompletion(task);
    }, [inSelectionMode, onToggleCompletion, onToggleSelection, task]);

    const priorityColor = theme.colors.accent[PRIORITY_TONE[task.priority]];

    return (
      <Pressable
        onPress={handlePress}
        onLongPress={handleLongPress}
        accessibilityRole="button"
        accessibilityLabel={task.title}
        accessibilityState={{ selected: isSelected, checked: completed }}
        style={({ pressed }) => [
          styles.row,
          {
            backgroundColor: pressed ? theme.colors.surface.tertiary : theme.colors.surface.primary,
            borderBottomColor: theme.colors.border.secondary,
          },
          isSelected && {
            backgroundColor: `${theme.colors.accent.indigo}1A`,
          },
        ]}
      >
        <Pressable
          onPress={handleCheckboxPress}
          accessibilityRole="checkbox"
          accessibilityLabel={
            inSelectionMode ? task.title : tasksStrings.toggleAccessibility(task.state)
          }
          accessibilityState={{ checked: inSelectionMode ? isSelected : completed }}
          hitSlop={8}
          style={[
            styles.checkbox,
            {
              borderColor:
                completed || isSelected ? theme.colors.accent.indigo : theme.colors.border.primary,
              backgroundColor: completed || isSelected ? theme.colors.accent.indigo : 'transparent',
            },
          ]}
        >
          {(completed || isSelected) && <Text style={styles.checkboxMark}>✓</Text>}
        </Pressable>
        <View style={styles.body}>
          <Text
            style={[
              styles.title,
              {
                color: completed ? theme.colors.text.tertiary : theme.colors.text.primary,
                textDecorationLine: completed ? 'line-through' : 'none',
              },
            ]}
            numberOfLines={2}
          >
            {task.title}
          </Text>
          <View style={styles.metaRow}>
            <View style={[styles.pill, { backgroundColor: `${priorityColor}26` }]}>
              <Text style={[styles.pillText, { color: priorityColor }]}>
                {tasksStrings.priority(task.priority)}
              </Text>
            </View>
            {task.dueAt !== null ? (
              <Text style={[styles.due, { color: theme.colors.text.secondary }]}>
                {tasksStrings.dueRelative(task.dueAt, nowMs ?? Date.now())}
              </Text>
            ) : null}
          </View>
        </View>
      </Pressable>
    );
  },
);

TaskRow.displayName = 'TaskRow';

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  } satisfies ViewStyle,
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1.5,
    marginTop: 2,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  } satisfies ViewStyle,
  checkboxMark: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 16,
  } satisfies TextStyle,
  body: {
    flex: 1,
  } satisfies ViewStyle,
  title: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 18,
  } satisfies TextStyle,
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
    flexWrap: 'wrap',
  } satisfies ViewStyle,
  pill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    marginRight: 8,
  } satisfies ViewStyle,
  pillText: {
    fontSize: 11,
    fontWeight: '600',
  } satisfies TextStyle,
  due: {
    fontSize: 12,
    fontWeight: '500',
  } satisfies TextStyle,
});
